<template>
  <div class="w-full min-h-full flex flex-col gap-5">

    <!-- ─── Hero: medidor tipo "speed test" ────────────────────────────── -->
    <div class="relative rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 bg-gradient-to-br from-slate-50 via-white to-indigo-50/60 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 px-6 py-8 sm:px-8 sm:py-10 flex flex-col items-center">
      <div class="absolute inset-0 dot-grid opacity-40 dark:opacity-30 pointer-events-none"></div>
      <div v-if="validating" class="absolute inset-0 pointer-events-none">
        <div class="scan-line"></div>
      </div>

      <p class="relative uppercase tracking-widest text-[0.62rem] text-slate-500 dark:text-slate-400 font-semibold m-0 mb-1">{{ subtitle }}</p>
      <h1 class="relative text-lg sm:text-xl font-bold text-slate-900 dark:text-white m-0 mb-7">{{ title }}</h1>

      <!-- Medidor -->
      <div class="relative gauge-wrap">
        <div class="gauge-glow" :style="{ '--color': gaugeColor }"></div>
        <div class="gauge-ring" :style="{ '--percent': gaugePercent, '--color': gaugeColor }"></div>
        <div class="gauge-needle" :style="{ '--color': gaugeColor, transform: `translateX(-50%) rotate(${needleDeg}deg)` }"></div>
        <div class="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <div class="gauge-icon-badge mb-1.5" :style="{ '--color': gaugeColor }">
            <i class="mdi text-lg" :class="[iconName, iconColorClass, validating ? 'animate-pulse' : '']"></i>
          </div>
          <p class="text-lg sm:text-xl font-extrabold m-0 leading-tight" :class="iconColorClass">{{ statusLabel }}</p>
          <p class="text-[0.7rem] mt-1 mb-0 max-w-[11rem] leading-snug" :class="validating ? 'text-slate-500 dark:text-slate-400' : iconColorClass">
            <span v-if="validating" class="typing-phrase">{{ funPhraseText }}</span>
            <span v-else>{{ headline }}</span>
          </p>
        </div>
      </div>

      <!-- Fila de pasos (estilo "server info" de un speed test) -->
      <div class="relative flex items-center gap-1 sm:gap-2 mt-3 mb-7 flex-wrap justify-center">
        <template v-for="(step, i) in steps" :key="step.key">
          <div class="flex flex-col items-center gap-1.5 w-16">
            <div class="relative">
              <div
                class="w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300"
                :class="gaugeStepWrapClass(step.status)"
              >
                <i v-if="step.status === 'checking'" class="mdi mdi-loading animate-spin text-[0.9rem]" :class="gaugeStepIconClass(step.status)"></i>
                <i v-else class="mdi text-[0.9rem]" :class="[stepIcon(step.key), gaugeStepIconClass(step.status)]"></i>
              </div>
              <!-- Insignia de resultado: check/alerta/X bien visibles, no solo el color del aro. -->
              <span
                v-if="step.status === 'ok' || step.status === 'warn' || step.status === 'error'"
                class="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white dark:border-slate-900 shadow-sm"
                :class="resultBadgeClass(step.status)"
              >
                <i class="mdi text-[0.75rem] leading-none text-white" :class="resultBadgeIcon(step.status)"></i>
              </span>
            </div>
            <span class="text-[0.58rem] font-medium text-slate-500 dark:text-slate-400 text-center leading-tight">{{ step.shortLabel }}</span>
          </div>
          <div
            v-if="i < steps.length - 1"
            class="w-4 sm:w-7 h-[3px] mb-4 flex-shrink-0 rounded-full transition-colors duration-500"
            :class="lineFilled(i) ? 'bg-emerald-400 dark:bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'"
          ></div>
        </template>
      </div>

      <button
        type="button"
        :disabled="validating"
        @click="handleValidate"
        class="relative inline-flex items-center justify-center gap-2 rounded-xl px-6 py-2.5 text-sm font-semibold overflow-hidden
               transition-all duration-200 shadow-md shadow-emerald-500/20
               bg-emerald-500 text-slate-950 hover:bg-emerald-400 hover:shadow-lg hover:shadow-emerald-500/30 active:scale-[0.98]
               focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-900
               disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
      >
        <i class="mdi text-base" :class="validating ? 'mdi-loading animate-spin' : 'mdi-lan-connect'"></i>
        <span>{{ validating ? 'Validando…' : actionLabel }}</span>
      </button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onUnmounted, nextTick } from 'vue'
import { getKeyOperation } from '@/api/MTCService.js'

defineProps({
  title: {
    type: String,
    default: 'Conexión con el MTC'
  },
  subtitle: {
    type: String,
    default: 'Diagnóstico de enlace · MTC'
  },
  actionLabel: {
    type: String,
    default: 'Validar conexión del MTC'
  }
})


// ─── Estado de validación ───────────────────────────────────────────────
const validating = ref(false)
const lastCheckedAt = ref('')
const testCount = ref(0)
// 'idle' | 'ok' | 'warn' | 'error'
const overallStatus = ref('idle')

// Un solo paso real: Inicio de Operación. Antes se probaban 4 canales
// (inicio + 2 consultas vehiculares + cierre); ahora solo se valida el de
// inicio — si ese responde, se considera todo correcto.
const steps = ref([
  {
    key: 'codigo',
    label: 'Inicio de Operación',
    shortLabel: 'Inicio',
    idleDetail: 'AutentificaInicioOperacion — autenticación con el MTC',
    status: 'idle',
    detail: '',
    ms: null,
  },
])

const history = ref([])

// ── Consola de diagnóstico ────────────────────────────────────────────────
const logLines = ref([])
const logBoxRef = ref(null)

const nowLabel = () => new Date().toLocaleTimeString('es-PE', { hour12: false })

const pushLog = async (text, type = 'info') => {
  logLines.value.push({ text, type, time: nowLabel() })
  await nextTick()
  if (logBoxRef.value) logBoxRef.value.scrollTop = logBoxRef.value.scrollHeight
}

// Secuencia fija (no cíclica) que se recorre una vez a lo largo de los ~5s
// de "cargando" — da la sensación de un chequeo real en varias etapas en
// vez de un resultado instantáneo, aunque la llamada real responda antes.
const LOADING_PHASES = [
  'Validando datos…',
  'Enviando solicitud al MTC…',
  'Esperando respuesta del servidor…',
  'Verificando código de operación…',
]
const MIN_VALIDATION_MS = 5000
const PHASE_MS = MIN_VALIDATION_MS / LOADING_PHASES.length

const funPhraseText = ref(LOADING_PHASES[0])
let phraseTimer = null

const startPhraseCycle = () => {
  let i = 0
  funPhraseText.value = LOADING_PHASES[0]
  phraseTimer = setInterval(() => {
    i += 1
    if (i >= LOADING_PHASES.length) {
      clearInterval(phraseTimer)
      phraseTimer = null
      return
    }
    funPhraseText.value = LOADING_PHASES[i]
  }, PHASE_MS)
}

const stopPhraseCycle = () => {
  clearInterval(phraseTimer)
  phraseTimer = null
}

const resetSteps = () => {
  steps.value.forEach((s) => {
    s.status = 'idle'
    s.detail = ''
    s.ms = null
  })
}

const formatNow = () => {
  return new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const handleValidate = async () => {
  if (validating.value) return
  validating.value = true
  overallStatus.value = 'idle'
  resetSteps()
  logLines.value = []
  testCount.value += 1
  startPhraseCycle()

  await pushLog('Iniciando protocolo de validación MTC…', 'system')

  // ── Único paso real: obtener código de operación (mtc/initday) ─────────
  const stepCodigo = steps.value[0]
  stepCodigo.status = 'checking'
  await pushLog('Enviando solicitud al MTC (mtc/initday)…')

  const t0 = performance.now()
  let resultStatus = 'error'
  let resultDetail = 'Sin respuesta del servicio'

  try {
    const response = await getKeyOperation()
    if (response.status === 200 || response.status === 201) {
      const codigoObtenido = response?.data?.data?.Codigo ?? null
      resultStatus = 'ok'
      resultDetail = codigoObtenido ? 'Código recibido correctamente' : 'Respuesta recibida sin código'
    } else {
      resultStatus = 'warn'
      resultDetail = `Respuesta inesperada (${response.status})`
    }
  } catch (error) {
    resultStatus = 'error'
    resultDetail = error.response?.data?.message || error.message || 'Sin respuesta del servicio'
  }

  const resultMs = Math.round(performance.now() - t0)

  // Deja correr el "cargando" el tiempo mínimo (~5s) aunque la respuesta
  // real haya llegado antes — así se ve la secuencia completa (validando →
  // enviando → esperando → verificando) en vez de un parpadeo instantáneo.
  // El paso se mantiene en "checking" (medidor en azul, girando) durante
  // toda esta espera; el resultado real recién se revela al final.
  const elapsed = performance.now() - t0
  if (elapsed < MIN_VALIDATION_MS) {
    await new Promise((resolve) => setTimeout(resolve, MIN_VALIDATION_MS - elapsed))
  }
  stopPhraseCycle()

  stepCodigo.status = resultStatus
  stepCodigo.detail = resultDetail
  stepCodigo.ms = resultMs
  overallStatus.value = resultStatus

  funPhraseText.value =
    resultStatus === 'ok' ? 'Todo correcto ✓' :
    resultStatus === 'warn' ? 'Respuesta con observaciones ⚠' :
    'No se pudo conectar ✗'

  await pushLog(
    resultStatus === 'ok' ? `✓ El MTC respondió: código recibido (${resultMs} ms)` :
    resultStatus === 'warn' ? `⚠ El MTC respondió con un estado inesperado (${resultDetail})` :
    `✗ Falló la solicitud de código (${resultDetail})`,
    resultStatus === 'ok' ? 'ok' : resultStatus
  )
  await pushLog(
    resultStatus === 'ok'
      ? 'Validación finalizada: conexión estable ✓'
      : resultStatus === 'warn'
        ? 'Validación finalizada: canal activo con observaciones ⚠'
        : 'Validación finalizada: sin conexión ✗',
    'system'
  )

  // Pausa breve para que se alcance a leer "Todo correcto" (o el mensaje de
  // error) antes de pasar a la vista de resultado final.
  await new Promise((resolve) => setTimeout(resolve, 700))

  lastCheckedAt.value = formatNow()
  history.value.unshift({
    id: `${Date.now()}`,
    time: lastCheckedAt.value,
    status: overallStatus.value,
    totalMs: resultMs,
  })
  if (history.value.length > 8) history.value = history.value.slice(0, 8)

  validating.value = false
}

onUnmounted(() => {
  stopPhraseCycle()
})

// ─── Helpers de presentación ────────────────────────────────────────────
const statusLabel = computed(() => {
  if (validating.value) return 'Verificando'
  if (overallStatus.value === 'ok') return 'Conectado'
  if (overallStatus.value === 'warn') return 'Con observaciones'
  if (overallStatus.value === 'error') return 'Sin conexión'
  return 'Sin verificar'
})


const headline = computed(() => {
  if (overallStatus.value === 'ok') return 'Conexión con el MTC estable'
  if (overallStatus.value === 'warn') return 'Canal activo, con observaciones de negocio'
  if (overallStatus.value === 'error') return 'No se pudo establecer conexión con el MTC'
  return 'Pulsa validar para comprobar la conexión con el MTC'
})

const iconName = computed(() => {
  if (validating.value) return 'mdi-satellite-variant'
  // Terminada la validación, se prioriza un ícono de resultado bien
  // reconocible (check / alerta / X) por encima del ícono "de red" — así se
  // lee de un vistazo si salió bien o mal, sin depender solo del color.
  if (overallStatus.value === 'ok') return 'mdi-check-circle'
  if (overallStatus.value === 'warn') return 'mdi-alert-circle'
  if (overallStatus.value === 'error') return 'mdi-close-circle'
  return 'mdi-access-point'
})

const iconColorClass = computed(() => {
  if (validating.value) return 'text-blue-600 dark:text-blue-400'
  if (overallStatus.value === 'ok') return 'text-emerald-600 dark:text-emerald-400'
  if (overallStatus.value === 'warn') return 'text-amber-600 dark:text-amber-400'
  if (overallStatus.value === 'error') return 'text-red-600 dark:text-red-400'
  return 'text-slate-500 dark:text-slate-400'
})

const stepIcon = (key) => {
  if (key === 'codigo') return 'mdi-key-variant'
  return 'mdi-circle-outline'
}

const gaugeStepWrapClass = (status) => {
  if (status === 'checking') return 'bg-blue-500/10 dark:bg-blue-500/15 border-blue-400/50 dark:border-blue-400/40'
  if (status === 'ok') return 'bg-emerald-500/10 dark:bg-emerald-500/15 border-emerald-400/50 dark:border-emerald-400/40'
  if (status === 'warn') return 'bg-amber-500/10 dark:bg-amber-500/15 border-amber-400/50 dark:border-amber-400/40'
  if (status === 'error') return 'bg-red-500/10 dark:bg-red-500/15 border-red-400/50 dark:border-red-400/40'
  return 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10'
}

// Insignia de resultado por paso (check/alerta/X) — se ve encima del ícono
// del paso, para que el resultado no dependa solo del color del borde.
const resultBadgeClass = (status) => {
  if (status === 'ok') return 'bg-emerald-500'
  if (status === 'warn') return 'bg-amber-500'
  if (status === 'error') return 'bg-red-500'
  return 'bg-slate-400'
}

const resultBadgeIcon = (status) => {
  if (status === 'ok') return 'mdi-check-bold'
  if (status === 'warn') return 'mdi-exclamation'
  if (status === 'error') return 'mdi-close-thick'
  return 'mdi-minus'
}

const gaugeStepIconClass = (status) => {
  if (status === 'checking') return 'text-blue-600 dark:text-blue-400'
  if (status === 'ok') return 'text-emerald-600 dark:text-emerald-400'
  if (status === 'warn') return 'text-amber-600 dark:text-amber-400'
  if (status === 'error') return 'text-red-600 dark:text-red-400'
  return 'text-slate-400 dark:text-slate-500'
}

// Progreso del medidor: cada paso terminado suma 1, el que está "checking"
// suma medio, para que la aguja se sienta en movimiento continuo.
const gaugePercent = computed(() => {
  const total = steps.value.length
  const avance = steps.value.reduce((acc, s) => {
    if (s.status === 'checking') return acc + 0.5
    if (s.status !== 'idle') return acc + 1
    return acc
  }, 0)
  return Math.min(100, Math.round((avance / total) * 100))
})

// Gauge abierto de 250° (de -125° a +125°, con el hueco abajo) — mismo
// sistema de ángulos para el arco (conic-gradient) y la aguja (rotate()).
const needleDeg = computed(() => -125 + (gaugePercent.value / 100) * 250)

const gaugeColor = computed(() => {
  if (validating.value) return '#60a5fa'
  if (overallStatus.value === 'ok') return '#34d399'
  if (overallStatus.value === 'warn') return '#fbbf24'
  if (overallStatus.value === 'error') return '#f87171'
  return '#475569'
})

// La línea entre el paso i y el i+1 se pinta apenas el paso i termina
// (con cualquier resultado, no solo éxito) — marca que ya se probó, no que
// haya salido bien; el color del icono ya indica el resultado.
const lineFilled = (i) => {
  const status = steps.value[i].status
  return status !== 'idle' && status !== 'checking'
}

</script>

<style scoped>
.scan-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(52, 211, 153, 0.8), transparent);
  animation: scan 1.8s linear infinite;
}

@keyframes scan {
  0% { top: -10%; }
  100% { top: 110%; }
}

.blink-cursor {
  animation: blink 1s step-start infinite;
}

@keyframes blink {
  50% { opacity: 0; }
}

.typing-phrase {
  display: inline-block;
}

.dot-grid {
  background-image: radial-gradient(rgba(148, 163, 184, 0.5) 1px, transparent 1px);
  background-size: 18px 18px;
}

/* ─── Medidor tipo "speed test" ────────────────────────────────────── */
.gauge-wrap {
  width: 240px;
  height: 240px;
  /* Track del aro sin recorrer: sutil en claro, tenue en oscuro. */
  --track: rgba(15, 23, 42, 0.08);
}

.dark .gauge-wrap {
  --track: rgba(255, 255, 255, 0.08);
}

/* Resplandor difuso detrás del aro, del color del estado — le da algo de
   "energía" al medidor sin depender de una imagen. */
.gauge-glow {
  position: absolute;
  inset: 14px;
  border-radius: 50%;
  background: var(--color);
  opacity: 0.16;
  filter: blur(22px);
  transition: background 0.6s ease;
  pointer-events: none;
}

.gauge-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(
    from -125deg,
    var(--color) 0deg,
    var(--color) calc(var(--percent) * 2.5deg),
    var(--track) calc(var(--percent) * 2.5deg),
    var(--track) 250deg,
    transparent 250deg,
    transparent 360deg
  );
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 18px), #000 calc(100% - 18px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 18px), #000 calc(100% - 18px));
  transition: background 0.6s ease;
}

.gauge-needle {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 3px;
  height: 78px;
  background: linear-gradient(to top, var(--color), transparent);
  transform-origin: bottom center;
  transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1), background 0.6s ease;
  border-radius: 3px;
  pointer-events: none;
}

/* Fondo circular sutil detrás del icono de estado, en el centro del medidor. */
.gauge-icon-badge {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(100, 116, 139, 0.12); /* respaldo si el navegador no soporta color-mix() */
  background: color-mix(in srgb, var(--color) 14%, transparent);
  transition: background 0.6s ease;
}
</style>
